import { Checkbox, FormControlLabel, styled } from '@ethings/ui-toolkit';
import { SxProps } from '@mui/system';
import { ChangeEvent, FC, ReactNode } from 'react';

interface SelectionProps {
  checked: boolean;
  'data-testid'?: string;
  disabled?: boolean;
  handleClick: (e: ChangeEvent<HTMLInputElement>) => void;
  indeterminate?: boolean;
  label: ReactNode;
  subItem?: boolean;
  sx?: SxProps;
  wrap?: boolean;
}

interface StyledFormControlLabelProps {
  subItem?: boolean;
  wrap?: boolean;
  sx?: SxProps;
}

const StyledFormControlLabel = styled(FormControlLabel, {
  shouldForwardProp: (prop) => prop !== 'subItem' && prop !== 'wrap',
})<StyledFormControlLabelProps>(({ subItem, wrap, theme, sx = {} }) => {
  const baseStyles = {
    margin: 0,
    width: '100%',
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    paddingLeft: subItem ? theme.spacing(3) : theme.spacing(2),
  };

  const labelStyles = wrap
    ? {
      '&': {
        alignItems: 'flex-start',
      },
      '& .MuiFormControlLabel-label': {
        color: subItem ? theme.palette.text.secondary : theme.palette.text.primary,
        textWrap: 'wrap' as const,
      },
    }
    : {
      '& .MuiFormControlLabel-label': {
        color: subItem ? theme.palette.text.secondary : theme.palette.text.primary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    };

  return {
    ...baseStyles,
    ...labelStyles,
    ...(sx as Record<string, unknown>),
  };
});

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  padding: 0,
  paddingRight: theme.spacing(1),
  color: theme.palette.grey[500],
}));

export const SelectionCheckBox: FC<SelectionProps> = ({ checked, handleClick, indeterminate, label, subItem, sx, disabled = false, wrap, ...rest }) => {
  return (
    <StyledFormControlLabel
      subItem={subItem}
      wrap={wrap}
      sx={sx}
      control={
        <StyledCheckbox
          checked={checked}
          disabled={disabled}
          indeterminate={indeterminate}
          onChange={handleClick}
          size="small"
        />
      }
      label={label}
      {...rest}
    />
  );
};
